import { sessionStorageKeys } from 'lib/consts/browserStorage'
import { getSSItem } from 'lib/helpers/getSSItem/getSSItem'
import { MfaMethod } from 'gql/types'

const isMfaMethod = (value: unknown): value is MfaMethod => value === MfaMethod.TOTP || value === MfaMethod.PHONE

/**
 * Stores the MFA methods the signed-in user has configured (from `AuthResponse.availableMethods`) in
 * `sessionStorage`, so `/login-verify` can decide which factor to challenge after the redirect from
 * `/login`, scoped to the tab and dropped when it closes. Like the paired email it carries no TTL, and
 * is cleared via `clearStoredMfaAuthData` when the flow completes. `get` returns null on absent or
 * malformed data, so a caller can safely fall back to a single-method default.
 */
export const storedMfaAvailableMethods = {
  set(methods: MfaMethod[]): void {
    window.sessionStorage.setItem(sessionStorageKeys.mfaAvailableMethods, JSON.stringify(methods))
  },

  get(): MfaMethod[] | null {
    const stored = getSSItem(sessionStorageKeys.mfaAvailableMethods)

    if (!stored) {
      return null
    }

    try {
      const parsed: unknown = JSON.parse(stored)

      if (!Array.isArray(parsed)) {
        return null
      }

      return parsed.filter(isMfaMethod)
    } catch {
      return null
    }
  },

  clear(): void {
    window.sessionStorage.removeItem(sessionStorageKeys.mfaAvailableMethods)
  },
}
